/**
 * TermsAcceptanceModal - Blocking modal for forced re-acceptance of updated terms
 *
 * Features:
 * - Full-screen blocking modal (no X button, no backdrop click close)
 * - Scrollable terms content
 * - Required checkbox before accepting
 * - Accept & Continue button (disabled until checked)
 * - Decline & Log Out button
 * - Keyboard accessible, screen-reader friendly
 */

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getTermsContent } from '../services/termsService';

export default function TermsAcceptanceModal() {
  const { termsConfig, acceptTerms, logout } = useAuth();
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [termsContent, setTermsContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(true);
  const contentRef = useRef(null);

  // Default terms content (fallback if Firestore unavailable)
  const DEFAULT_TERMS = {
    title: 'Terms of Service',
    version: '1.0.0',
    content: `By using QuickMeet, you agree to:

• Use the service lawfully and respectfully
• Maintain the security of your account
• Not harass or harm other users
• Not transmit malware or harmful content
• Respect intellectual property rights

We provide the service "as is" and may update these terms periodically. Continued use constitutes acceptance of any changes.

For the full Terms of Service, please visit the Terms page.`,
    summary: 'Standard terms of service for QuickMeet usage'
  };

  // Load terms content
  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoadingContent(true);
        if (termsConfig?.requiredVersion) {
          const content = await getTermsContent(termsConfig.requiredVersion, 'terms');
          // Use fetched content or fallback to default
          setTermsContent(content || DEFAULT_TERMS);
        } else {
          // No config yet, use defaults
          setTermsContent(DEFAULT_TERMS);
        }
      } catch (err) {
        console.error('Error loading terms content:', err);
        // Use fallback content instead of showing error
        setTermsContent(DEFAULT_TERMS);
      } finally {
        setLoadingContent(false);
      }
    };

    loadContent();
  }, [termsConfig?.requiredVersion]);

  const handleAccept = async () => {
    if (!checked) return;

    setError('');
    setLoading(true);

    try {
      await acceptTerms();
      // Modal will automatically close when needsTermsReaccept becomes false
    } catch (err) {
      console.error('Error accepting terms:', err);
      setError('Failed to accept terms. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    setLoading(true);
    try {
      await logout();
    } catch (err) {
      console.error('Error logging out:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && checked && !loading) {
      handleAccept();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-white dark:bg-[#1c1c1e] flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-modal-title"
      aria-describedby="terms-modal-description"
      onKeyDown={handleKeyDown}
    >
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-8 border-b border-[#d2d2d7] dark:border-[#2c2c2e]">
        <div className="max-w-2xl mx-auto text-center">
          {/* Icon */}
          <div className="w-16 h-16 bg-gradient-to-br from-[#06c] to-[#0055d4] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>

          <h1
            id="terms-modal-title"
            className="text-2xl sm:text-3xl font-semibold text-[#1d1d1f] dark:text-white mb-3"
          >
            We've Updated Our Terms
          </h1>

          <p
            id="terms-modal-description"
            className="text-[#6e6e73] dark:text-[#8e8e93] max-w-md mx-auto"
          >
            Please review and accept our updated Terms of Service and Privacy Policy to continue using QuickMeet.
          </p>
        </div>
      </div>

      {/* Scrollable Content */}
      <div
        ref={contentRef}
        className="flex-1 overflow-y-auto px-6 py-8"
      >
        <div className="max-w-2xl mx-auto">
          {loadingContent ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-[#06c]/30 border-t-[#06c] rounded-full animate-spin" />
            </div>
          ) : error && !termsContent ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-[#06c] text-white rounded-lg hover:bg-[#0055d4] transition-colors"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-2xl p-6">
              {/* Terms Summary */}
              {termsContent?.summary && (
                <div className="mb-6 pb-6 border-b border-[#d2d2d7] dark:border-[#424245]">
                  <h2 className="text-lg font-semibold text-[#1d1d1f] dark:text-white mb-2">
                    What's Changed
                  </h2>
                  <p className="text-[#6e6e73] dark:text-[#8e8e93] text-sm">
                    {termsContent.summary}
                  </p>
                </div>
              )}

              {/* Terms Content Preview */}
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <p className="text-[#6e6e73] dark:text-[#8e8e93] text-sm mb-4">
                  Version {termsConfig?.requiredVersion || '1.0.0'} - Effective{' '}
                  {termsContent?.effectiveDate
                    ? new Date(termsContent.effectiveDate).toLocaleDateString()
                    : 'immediately'}
                </p>

                {/* Show excerpt or full content */}
                <div className="text-[#1d1d1f] dark:text-[#e5e5ea] text-sm leading-relaxed whitespace-pre-wrap">
                  {termsContent?.content
                    ? termsContent.content.substring(0, 2000) + (termsContent.content.length > 2000 ? '...' : '')
                    : 'Terms content is loading...'}
                </div>
              </div>

              {/* View Full Terms Link */}
              <div className="mt-6 pt-6 border-t border-[#d2d2d7] dark:border-[#424245] flex gap-4">
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#06c] hover:underline text-sm font-medium"
                >
                  View Full Terms of Service
                </Link>
                <Link
                  to="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#06c] hover:underline text-sm font-medium"
                >
                  View Privacy Policy
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer with Checkbox and Buttons */}
      <div className="flex-shrink-0 px-6 py-6 border-t border-[#d2d2d7] dark:border-[#2c2c2e] bg-white dark:bg-[#1c1c1e]">
        <div className="max-w-2xl mx-auto">
          {/* Error Message */}
          {error && termsContent && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Checkbox */}
          <label className="flex items-start gap-3 mb-6 cursor-pointer group">
            <div className="relative flex-shrink-0 mt-0.5">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                disabled={loading || loadingContent}
                className="sr-only peer"
              />
              <div
                className={`w-5 h-5 rounded-md border-2 transition-all duration-200
                  ${checked
                    ? 'bg-[#06c] border-[#06c]'
                    : 'border-[#d2d2d7] bg-white group-hover:border-[#06c]'
                  }
                  peer-focus-visible:ring-2 peer-focus-visible:ring-[#06c] peer-focus-visible:ring-offset-2
                `}
              >
                {checked && (
                  <svg
                    className="w-full h-full text-white p-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
            </div>
            <span className="text-sm text-[#1d1d1f] dark:text-white leading-relaxed">
              I have read and agree to the updated{' '}
              <Link
                to="/terms"
                target="_blank"
                className="text-[#06c] hover:underline font-medium"
                onClick={(e) => e.stopPropagation()}
              >
                Terms of Service
              </Link>
              {' '}and{' '}
              <Link
                to="/privacy"
                target="_blank"
                className="text-[#06c] hover:underline font-medium"
                onClick={(e) => e.stopPropagation()}
              >
                Privacy Policy
              </Link>
            </span>
          </label>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleAccept}
              disabled={!checked || loading || loadingContent}
              className="flex-1 px-6 py-3.5 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white rounded-xl font-medium hover:shadow-xl hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                'Accept & Continue'
              )}
            </button>

            <button
              onClick={handleDecline}
              disabled={loading}
              className="px-6 py-3.5 bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white rounded-xl font-medium hover:bg-[#e8e8ed] dark:hover:bg-[#424245] transition-all disabled:opacity-50"
            >
              Decline & Log Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
